import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { URL as API_URL } from '../../../env';

// Lead-capture gate for the 2-minute demo, mirroring the VC sandbox's gate in demos/kestrel-demo.html
// — same copy, same fields, same watermark contract.
//
// NOTE ON THE FORM DATA
// The details are POSTed once to /api/sandbox/entry, which emails them to the team and returns the
// caller's IP for the watermark. Nothing is written to the database — that email is the only
// record. Client-side, the values live in this tab's sessionStorage only so the watermark can be
// rebuilt as the visitor moves between steps.

// Reason: kept separate from the VC sandbox's own keys. Both pages are same-origin, so shared keys
// would silently unlock one sandbox because the visitor passed the other.
export const GATE_KEY = 'dlg-journey-gate-passed';
export const WATERMARK_KEY = 'dlg-journey-gate-watermark';

// Common free/personal providers — not exhaustive, but covers the overwhelming majority of real
// personal addresses. Extend by adding the domain, lowercase, no leading "@".
const PERSONAL_EMAIL_DOMAINS = [
    'gmail.com', 'googlemail.com',
    'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'ymail.com', 'rocketmail.com',
    'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'live.com', 'live.co.uk', 'msn.com',
    'icloud.com', 'me.com', 'mac.com',
    'aol.com',
    'protonmail.com', 'proton.me', 'pm.me',
    'mail.com', 'gmx.com', 'gmx.co.uk',
    'zoho.com',
    'yandex.com', 'yandex.ru',
    'qq.com', '163.com', '126.com',
];

const FIELDS = [
    { name: 'firstName', label: 'First name', type: 'text', autoComplete: 'given-name' },
    { name: 'lastName', label: 'Last name', type: 'text', autoComplete: 'family-name' },
    { name: 'email', label: 'Work email address', type: 'email', autoComplete: 'email', full: true, hint: 'Please use your company email — not a personal address (Gmail, Yahoo, etc.)' },
    { name: 'title', label: 'Job title', type: 'text', autoComplete: 'organization-title' },
    { name: 'company', label: 'Company', type: 'text', autoComplete: 'organization' },
];

const EMPTY = { firstName: '', lastName: '', email: '', title: '', company: '' };

const isPersonalEmail = (email) => {
    const domain = email.split('@')[1];
    return !!domain && PERSONAL_EMAIL_DOMAINS.includes(domain.trim().toLowerCase());
};

/**
 * Records the access request — the server emails the details to the team and returns the caller's
 * IP for the watermark. Resolves to a placeholder on any failure so the visitor is never blocked
 * by a mail or network problem.
 *
 * @param {object} data - the submitted form values
 * @returns {Promise<string>} the caller's IP, or 'IP unavailable'
 */
const recordEntry = (data) => fetch(`${API_URL}/api/sandbox/entry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...data, source: 'Companies 2-minute demo' }),
    cache: 'no-store',
})
    .then((r) => r.json())
    .then((j) => (j && j['body'] && j['body']['ip']) || 'IP unavailable')
    .catch(() => 'IP unavailable');

const formatWatermark = (data, ip) => {
    const stamp = `${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC`;
    return `${data['email']}   ·   ${ip}   ·   ${stamp}`;
};

/**
 * @param {function} onPass  - called with the watermark string once the form validates
 * @param {function} onClose - called when the visitor dismisses the gate
 */
const JourneyGate = ({ onPass, onClose }) => {
    const [values, setValues] = useState(EMPTY);
    const [error, setError] = useState('');
    const [isBusy, setIsBusy] = useState(false);
    const firstFieldRef = useRef(null);

    useEffect(() => {
        if (firstFieldRef.current) firstFieldRef.current.focus();
        const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const setField = (name) => (e) => setValues({ ...values, [name]: e['target']['value'] });

    const handleSubmit = (e) => {
        e.preventDefault();
        const data = Object.keys(EMPTY).reduce((acc, k) => ({ ...acc, [k]: (values[k] || '').trim() }), {});

        if (!Object.values(data).every((v) => v.length > 0) || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data['email'])) {
            return setError('Please fill in every field with a valid email address.');
        }
        if (isPersonalEmail(data['email'])) {
            return setError("Please use your work email address — personal addresses (Gmail, Yahoo, etc.) aren't accepted.");
        }

        setError('');
        setIsBusy(true);
        recordEntry(data).then((ip) => onPass(formatWatermark(data, ip)));
    };

    // Portalled to <body>: the demo panel sits inside a sticky, overflow:hidden section, and later
    // sections on the page open their own stacking contexts — so a z-index inside the panel is not
    // enough and page content paints over the card. The wrapper re-applies .dlg-journey-demo so the
    // scoped selectors and the panel's CSS variables still reach the gate.
    return createPortal((
        <div className="dlg-journey-demo jd-gate-portal">
        <div className="jd-gate" role="dialog" aria-modal="true" aria-label="Before you dive in">
            <div className="jd-gate-card">
                <button type="button" className="jd-gate-close" aria-label="Close" onClick={onClose}>
                    <svg viewBox="0 0 14 14" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                </button>

                <div className="jd-gate-brand">
                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
                        <path d="M2 16L8 8L13 14L24 2" stroke="#FB7785" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2 20L9 12L14 18L24 6" stroke="#4A67FF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>Diligentsia</span>
                </div>

                <h2 className="jd-gate-title">Before you dive in</h2>
                <p className="jd-gate-sub">This is a live interactive sandbox of the Diligentsia deal workspace. Tell us a little about you and it's all yours. 2 minute demo.</p>

                <form className="jd-gate-form" onSubmit={handleSubmit} noValidate>
                    <div className="jd-gate-only-inputs">Your only inputs</div>
                    <div className="jd-gate-grid">
                        {FIELDS.map((f, i) => (
                            <label key={f['name']} className={`jd-gate-field${f['full'] ? ' is-full' : ''}`}>
                                {f['label']}
                                <input
                                    ref={i === 0 ? firstFieldRef : undefined}
                                    type={f['type']}
                                    value={values[f['name']]}
                                    autoComplete={f['autoComplete']}
                                    onChange={setField(f['name'])}
                                />
                                {f['hint'] && <span className="jd-gate-hint">{f['hint']}</span>}
                            </label>
                        ))}
                    </div>
                    {error && <p className="jd-gate-error">{error}</p>}
                    <button type="submit" className="jd-gate-submit" disabled={isBusy}>
                        {isBusy ? 'Just a moment…' : 'Enter the sandbox →'}
                    </button>
                </form>

                <p className="jd-gate-notice">
                    🔒 For security, every screen in this sandbox is watermarked with your email address, IP address, and access time. Please don't screenshot or share outside your organisation.
                    <span className="jd-gate-copyright">© 2026 Diligentsia Limited. All rights reserved. Unauthorised reproduction or distribution of this content is prohibited.</span>
                </p>
                <p className="jd-gate-footnote">
                    Sample data only — nothing you do in the sandbox affects a real deal. See our <a href="/policy-privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
                </p>
            </div>
        </div>
        </div>
    ), document.body);
};

export default JourneyGate;
